import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Navbar, Nav, Modal } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import axios from "axios";
import { AuthContext } from "../AuthContext";
import { parseStoredAccessToken } from "../parseStoredAccessToken";
import { site_url } from "../config";

const OPERATORS = [
  { name: "Airtel", service: "Prepaid", skey: "AT" },
  { name: "V!", service: "Prepaid", skey: "VI" },
  { name: "Reliance Jio", service: "Prepaid", skey: "RJ" },
  { name: "BSNL-TalkTime", service: "Prepaid", skey: "BT" },
  { name: "BSNL-Special", service: "Prepaid", skey: "BS" },
  { name: "Airtel Digital TV", service: "DTH", skey: "AT" },
  { name: "Dish TV", service: "DTH", skey: "DT" },
  { name: "Videocon D2H", service: "DTH", skey: "VT" },
  { name: "Tata Play (Tata Sky)", service: "DTH", skey: "TT" },
  { name: "Sun Direct", service: "DTH", skey: "ST" },
];

const formatTalktime = (value) => {
  const text = String(value ?? "").trim();
  const num = Number(text);
  if (text === "" || !Number.isFinite(num)) return "0.00";
  return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRechargeWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function PhoneDth() {
  const { user, loading, fulluser } = useContext(AuthContext);
  const [service, setService] = useState("Prepaid");
  const [skey, setSkey] = useState("AT");
  const [accountNo, setAccountNo] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyMore, setHistoryMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [serverPaging, setServerPaging] = useState(false);
  const [historyShown, setHistoryShown] = useState(10);
  const [circle, setCircle] = useState(null);
  const [lookupNote, setLookupNote] = useState("");
  const [plans, setPlans] = useState([]);
  const [plansNote, setPlansNote] = useState("");
  const [showPlans, setShowPlans] = useState(false);
  const [planQuery, setPlanQuery] = useState("");
  const [planType, setPlanType] = useState("All");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [dthInfo, setDthInfo] = useState(null);
  const [showWalletInfo, setShowWalletInfo] = useState(false);
  const walletInfoChecked = useRef(false);
  const [rules, setRules] = useState({
    phone_enabled: true,
    dth_enabled: true,
    max_recharge: 50001,
    monthly_recharge_limit: 50001,
    wallet_min_amount: 0,
    wallet_balance: null,
    monthly_used: null,
    monthly_remaining: null,
    instant_bonus_percent: 0,
    instant_bonus_expire_days: 0,
    welcome_bonus: 0,
    wallet_excludes_welcome: false,
  });

  const operators = useMemo(
    () => OPERATORS.filter((item) => item.service === service),
    [service]
  );

  const planTypes = useMemo(() => {
    const counts = new Map();
    plans.forEach((plan) => {
      const type = String(plan.type || "Other").trim() || "Other";
      counts.set(type, (counts.get(type) || 0) + 1);
    });
    return Array.from(counts.entries());
  }, [plans]);

  const visiblePlans = useMemo(() => {
    const query = planQuery.trim().toLowerCase();
    return plans
      .map((plan, index) => ({ ...plan, index, typeLabel: String(plan.type || "Other").trim() || "Other" }))
      .filter((plan) => planType === "All" || plan.typeLabel === planType)
      .filter((plan) => {
        if (!query) return true;
        return [plan.amount, plan.validity, plan.description, plan.talktime, plan.typeLabel]
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
  }, [plans, planQuery, planType]);

  const services = ["Prepaid", "DTH"].filter((item) =>
    item === "Prepaid" ? rules.phone_enabled : rules.dth_enabled
  );

  const clearPlanAmount = () => {
    setAmount("");
    setSelectedPlan(null);
    setPlans([]);
    setPlansNote("");
    setPlanQuery("");
    setPlanType("All");
    setShowPlans(false);
  };

  const onServiceChange = (next) => {
    setService(next);
    const first = OPERATORS.find((item) => item.service === next);
    setSkey(first ? first.skey : "");
    setAccountNo("");
    setCircle(null);
    setLookupNote("");
    setDthInfo(null);
    setError("");
    setResult(null);
    clearPlanAmount();
  };

  const authHeaders = () => {
    const token = parseStoredAccessToken(user);
    return {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };
  };

  const loadHistory = async (page = 1) => {
    const token = parseStoredAccessToken(user);
    if (!token) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${site_url}/recharge/history`, {
        headers: authHeaders(),
        params: { page },
      });
      const rows = res.data?.history || [];
      const paged = typeof res.data?.has_more === "boolean";
      setServerPaging(paged);
      setHistory((prev) => {
        if (page === 1) return rows;
        const seen = new Set(prev.map((row) => row.orderid));
        return [...prev, ...rows.filter((row) => !seen.has(row.orderid))];
      });
      setHistoryPage(page);
      setHistoryMore(paged ? res.data.has_more : false);
      if (page === 1) setHistoryShown(10);
    } catch (err) {
      if (page === 1) setHistory([]);
      setHistoryMore(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadRules = async () => {
    try {
      const operatorsRes = await axios.get(`${site_url}/recharge/operators`);
      if (operatorsRes.data?.settings) {
        setRules((prev) => ({ ...prev, ...operatorsRes.data.settings }));
      }
    } catch (err) {
      // Keep defaults when the settings API is not deployed yet.
    }

    const token = parseStoredAccessToken(user);
    if (!token) return;
    try {
      const details = await axios.get(`${site_url}/customer_details`, {
        headers: authHeaders(),
      });
      const data = details.data?.data;
      const points = data?.redemption_total;
      const bonusSetting = data?.app_setting;
      if (data && !walletInfoChecked.current) {
        walletInfoChecked.current = true;
        const hasFirstBill = (Array.isArray(data.billlist) && data.billlist.length > 0)
          || Number(data.welcome_bonus_used || 0) === 1;
        if (!hasFirstBill) setShowWalletInfo(true);
      }
      if (points != null || bonusSetting) {
        setRules((prev) => ({
          ...prev,
          ...(points != null
            ? {
                wallet_balance: Number(points),
                welcome_bonus: Number(data?.welcome_bonus_val || 0),
                wallet_excludes_welcome: false,
              }
            : {}),
          ...(bonusSetting
            ? {
                instant_bonus_percent: Number(bonusSetting.instat_bonus || 0),
                instant_bonus_expire_days: Number(bonusSetting.instance_expried || 0),
              }
            : {}),
        }));
      }
    } catch (err) {
      // Wallet points stay hidden until customer details load.
    }
    try {
      const res = await axios.get(`${site_url}/recharge/limits`, {
        headers: authHeaders(),
      });
      setRules((prev) => ({
        ...prev,
        ...(res.data?.settings || {}),
        wallet_balance: res.data?.wallet_balance ?? prev.wallet_balance,
        wallet_excludes_welcome: Boolean(res.data?.wallet_excludes_welcome),
        monthly_used: res.data?.monthly_used ?? null,
        monthly_remaining: res.data?.monthly_remaining ?? null,
        instant_bonus_percent: res.data?.instant_bonus_percent ?? prev.instant_bonus_percent,
        instant_bonus_expire_days: res.data?.instant_bonus_expire_days ?? prev.instant_bonus_expire_days,
      }));
    } catch (err) {
      // Limits need a signed-in customer on the API that has this route.
    }
  };

  useEffect(() => {
    loadHistory();
    loadRules();
  }, [user]);

  useEffect(() => {
    if (!services.includes(service) && services.length > 0) {
      onServiceChange(services[0]);
    }
  }, [rules.phone_enabled, rules.dth_enabled]);

  useEffect(() => {
    if (service !== "Prepaid" || !/^[6-9]\d{9}$/.test(accountNo.trim())) {
      setCircle(null);
      setLookupNote("");
      return;
    }
    const token = parseStoredAccessToken(user);
    if (!token) return;
    setCircle(null);
    let cancel = false;
    const timer = setTimeout(async () => {
      setLookupNote("Finding operator...");
      try {
        const res = await axios.post(
          `${site_url}/recharge/lookup`,
          { mobile: accountNo.trim() },
          { headers: authHeaders() }
        );
        if (cancel) return;
        if (res.data?.skey) setSkey(res.data.skey);
        setCircle({
          name: res.data?.circle || "",
          cir: res.data?.cir || "",
          code: res.data?.circle_code || "",
        });
        setLookupNote(
          [res.data?.operator_name, res.data?.circle].filter(Boolean).join(" · ")
        );
      } catch (err) {
        if (cancel) return;
        setCircle(null);
        setLookupNote(err.response?.data?.message || "Operator lookup failed.");
      }
    }, 400);
    return () => {
      cancel = true;
      clearTimeout(timer);
    };
  }, [accountNo, service, user]);

  useEffect(() => {
    const token = parseStoredAccessToken(user);
    if (!token || !skey) {
      setPlans([]);
      return;
    }
    if (service === "Prepaid" && !circle?.cir) {
      setPlans([]);
      setPlansNote("");
      return;
    }
    let cancel = false;
    const timer = setTimeout(async () => {
      setSelectedPlan(null);
      setPlanType("All");
      setPlansNote("Loading plans...");
      try {
        const res = await axios.post(
          `${site_url}/recharge/plans`,
          {
            skey,
            service,
            cir: service === "Prepaid" ? circle?.cir : undefined,
            circle_code: service === "Prepaid" ? circle?.code : undefined,
          },
          { headers: authHeaders() }
        );
        if (cancel) return;
        const rows = res.data?.plans || [];
        setPlans(rows);
        setPlansNote(rows.length ? "" : "No plans found for this operator.");
      } catch (err) {
        if (cancel) return;
        setPlans([]);
        setPlansNote(err.response?.data?.message || "Plans could not be loaded.");
      }
    }, 300);
    return () => {
      cancel = true;
      clearTimeout(timer);
    };
  }, [skey, service, circle, user]);

  useEffect(() => {
    if (service !== "DTH" || accountNo.trim().length < 5) {
      setDthInfo(null);
      return;
    }
    const token = parseStoredAccessToken(user);
    if (!token) return;
    let cancel = false;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${site_url}/recharge/dth-info`,
          { skey, service, account_no: accountNo.trim() },
          { headers: authHeaders() }
        );
        if (!cancel) {
          const customer = res.data?.customer;
          const hasCustomer = customer && (customer.name || customer.balance || (customer.details || []).length > 0);
          if (hasCustomer) setDthInfo(customer);
          else setDthInfo(null);
        }
      } catch (err) {
        if (!cancel) setDthInfo(null);
      }
    }, 500);
    return () => {
      cancel = true;
      clearTimeout(timer);
    };
  }, [accountNo, skey, service, user]);

  if (loading) {
    return <div style={{ minHeight: "100vh", background: "#f4f7fb" }} />;
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  const rechargeAmount = Number(amount) || 0;
  const welcomeHeld = Number(rules.welcome_bonus) || 0;
  const walletBalance = rules.wallet_balance == null
    ? null
    : Math.max(0, Number(rules.wallet_balance) - (rules.wallet_excludes_welcome ? 0 : welcomeHeld));
  let walletUse = 0;
  let onlineUse = 0;
  if (walletBalance != null && rechargeAmount >= 10) {
    if (walletBalance >= rechargeAmount) {
      walletUse = rechargeAmount;
    } else if (walletBalance > 0) {
      onlineUse = Math.round((rechargeAmount - walletBalance) * 100) / 100;
      walletUse = Math.round((rechargeAmount - onlineUse) * 100) / 100;
      if (onlineUse < 1) {
        onlineUse = 1;
        walletUse = Math.round((rechargeAmount - 1) * 100) / 100;
        if (walletUse > walletBalance || walletUse <= 0) {
          walletUse = 0;
          onlineUse = rechargeAmount;
        }
      }
    } else {
      onlineUse = rechargeAmount;
    }
  }
  const autoMode = walletUse > 0 && onlineUse > 0 ? "partial" : walletUse > 0 ? "wallet" : "razorpay";
  const rupee = (value) => `₹${Number(value).toLocaleString("en-IN")}`;
  const maxRecharge = Number(rules.max_recharge) || 0;
  const maxRechargeReached = autoMode !== "razorpay" && rechargeAmount > maxRecharge;
  const maxRechargeLabel = maxRecharge.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const visibleHistory = serverPaging ? history : history.slice(0, historyShown);
  const canLoadMore = serverPaging ? historyMore : history.length > historyShown;
  const bonusPercent = Number(rules.instant_bonus_percent) || 0;
  const bonusDays = Number(rules.instant_bonus_expire_days) || 0;
  const instantBonus = rechargeAmount > 0 && bonusPercent > 0
    ? Math.round(((rechargeAmount * bonusPercent) / 100) * 100) / 100
    : 0;

  const submitRecharge = async (mode, walletAmount) => {
    setError("");
    setResult(null);
    setSubmitting(true);
    let openedCheckout = false;
    try {
      const res = await axios.post(
        `${site_url}/recharge/process`,
        {
          skey,
          service,
          account_no: accountNo.trim(),
          amount,
          pay_mode: mode,
          wallet_amount: mode === "partial" ? walletAmount : undefined,
        },
        { headers: authHeaders() }
      );

      if (res.data?.status === "pay") {
        openedCheckout = true;
        openRazorpay(res.data);
        return;
      }

      setResult(res.data);
      setAccountNo("");
      setAmount("");
      loadHistory();
      loadRules();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.account_no?.[0] ||
        err.response?.data?.errors?.amount?.[0] ||
        err.response?.data?.errors?.wallet_amount?.[0] ||
        "Recharge could not be sent. Please try again.";
      setError(message);
    } finally {
      if (!openedCheckout) setSubmitting(false);
    }
  };

  const openRazorpay = (data) => {
    if (!window.Razorpay) {
      setError("Razorpay checkout is not available.");
      setSubmitting(false);
      return;
    }

    const options = {
      key: data.razorpay_key,
      amount: data.amount,
      currency: data.currency || "INR",
      name: "APM",
      description: "Phone / DTH recharge",
      order_id: data.order_id,
      handler: async function (response) {
        try {
          const paid = await axios.post(
            `${site_url}/recharge/verify-payment`,
            {
              orderid: data.recharge_orderid,
              razorpay_payment_id: response.razorpay_payment_id,
            },
            { headers: authHeaders() }
          );
          setResult(paid.data);
          setAccountNo("");
          setAmount("");
          loadHistory();
          loadRules();
        } catch (err) {
          setError(err.response?.data?.message || "Payment verification failed.");
        } finally {
          setSubmitting(false);
        }
      },
      prefill: {
        name: fulluser?.username || "",
        contact: fulluser?.phone || "",
      },
      theme: { color: "#5ca4d6" },
      modal: {
        ondismiss: function () {
          setSubmitting(false);
          setError("Payment was closed before it finished.");
        },
      },
    };

    const checkout = new window.Razorpay(options);
    checkout.on("payment.failed", function (response) {
      setSubmitting(false);
      setError(response?.error?.description || "Razorpay payment failed.");
    });
    checkout.open();
  };

  const selected = OPERATORS.find((item) => item.skey === skey && item.service === service);

  return (
    <div style={{ minHeight: "100vh", background: "#f4f7fb", paddingBottom: 40 }}>
      <Navbar expand="lg" className="px-3" style={navStyle}>
        <Navbar.Brand className="fw-bold text-white">
          <FaArrowLeft
            onClick={() => window.history.back()}
            className="fs-5 text-white"
            style={{ cursor: "pointer", marginRight: 8 }}
          />
          Phone / DTH Recharge
        </Navbar.Brand>
        <Nav className="ms-auto" />
      </Navbar>

      <div className="container" style={{ maxWidth: 520, marginTop: 20 }}>
        <div className="card shadow-sm" style={{ borderRadius: 16, border: "none" }}>
          <div className="card-body p-4">
            {services.length === 0 ? (
              <div className="alert alert-warning mb-0">Phone and DTH recharge are turned off.</div>
            ) : (
            <>
            <div className="d-flex mb-3" style={{ gap: 8 }}>
              {services.map((item) => (
                <button
                  key={item}
                  type="button"
                  className="btn flex-fill"
                  onClick={() => onServiceChange(item)}
                  style={{
                    borderRadius: 12,
                    background: service === item ? "#5ca4d6" : "#eef3f8",
                    color: service === item ? "#fff" : "#335",
                    border: "none",
                    fontWeight: 600,
                  }}
                >
                  {item}
                </button>
              ))}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (autoMode === "partial") submitRecharge("partial", walletUse);
                else if (autoMode === "wallet") submitRecharge("wallet");
                else submitRecharge("razorpay");
              }}
            >
              <label className="form-label fw-semibold">Operator</label>
              <select
                className="form-select mb-3"
                value={skey}
                onChange={(e) => {
                  setSkey(e.target.value);
                  setSelectedPlan(null);
                  setAmount("");
                  setPlanType("All");
                  setShowPlans(false);
                }}
                required
              >
                {operators.map((item) => (
                  <option key={item.skey} value={item.skey}>
                    {item.name} ({item.skey})
                  </option>
                ))}
              </select>

              <label className="form-label fw-semibold">
                {service === "Prepaid" ? "Mobile number" : "DTH subscriber number"}
              </label>
              <input
                className="form-control mb-3"
                inputMode="numeric"
                value={accountNo}
                onChange={(e) => setAccountNo(e.target.value)}
                placeholder={service === "Prepaid" ? "10-digit mobile number" : "Subscriber / account number"}
                required
              />
              {lookupNote && <p className="text-muted small mb-3">{lookupNote}</p>}
              {dthInfo && (
                <div className="mb-3 p-3" style={{ background: "#f4f7fb", borderRadius: 12 }}>
                  {dthInfo.error ? (
                    <div className="small">{dthInfo.error}</div>
                  ) : (
                    <>
                      {dthInfo.name && <div className="fw-semibold">{dthInfo.name}</div>}
                      {dthInfo.balance && <div className="small">Balance {dthInfo.balance}</div>}
                      {(dthInfo.details || []).slice(0, 6).map((row) => (
                        <div key={row.label} className="small d-flex justify-content-between">
                          <span>{row.label}</span>
                          <span>{row.value}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              <label className="form-label fw-semibold">
              Recharge Plans (₹10 – ₹{Number(rules.max_recharge).toLocaleString("en-IN")})
              </label>
              <input
                className="form-control mb-3"
                type="number"
                min="10"
                max={maxRecharge}
                step="1"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setSelectedPlan(null);
                }}
                placeholder="Amount"
                required
              />
            
              {selectedPlan && (
                <div className="mb-3 p-3" style={{ background: "#f4f7fb", borderRadius: 12 }}>
                  <div className="d-flex justify-content-between">
                    <strong>₹{Number(selectedPlan.amount).toLocaleString("en-IN")}</strong>
                    {service === "Prepaid" && (
                      <div className="small mt-1">Talktime : {formatTalktime(selectedPlan.talktime)}</div>
                    )}

                    {selectedPlan.validity && <span className="small text-muted"> {selectedPlan.validity}</span>}
                  </div>
                  {selectedPlan.description && <div className="small text-muted mt-1" style={{ whiteSpace: "pre-line" }}>{selectedPlan.description}</div>}
              
                </div>
              )}
              
              
              {plansNote && <p className="text-muted small mb-2">{plansNote}</p>}

            
              {plans.length > 0 && (
                <button
                  type="button"
                  className="btn w-100 mb-3"
                  style={{ borderRadius: 12, fontWeight: 700, border: "1px solid #5ca4d6", color: "#5ca4d6" }}
                  onClick={() => {
                    setPlanQuery("");
                    setPlanType("All");
                    setShowPlans(true);
                  }}
                >
                  View {plans.length} plans
                </button>
              )}
              <p className="text-muted small mb-3">
                Monthly limit {Number(rules.monthly_recharge_limit)} {Number(rules.monthly_recharge_limit) === 1 ? "time" : "times"}
                {rules.monthly_remaining != null
                  ? ` · ${Number(rules.monthly_remaining)} left this month`
                  : ""}
                . Wallet minimum coins {Number(rules.wallet_min_amount).toLocaleString("en-IN")}
                {rules.wallet_balance != null
                  ? ` · your wallet coins ${Number(walletBalance).toLocaleString("en-IN")}`
                  : ""}
                .{" "}
                <button
                  type="button"
                  onClick={() => setShowWalletInfo(true)}
                  style={{ border: "none", background: "none", color: "#5ca4d6", fontWeight: 700, padding: 0 }}
                >
                 Recharge T&C
                </button>
              </p>
              {rechargeAmount >= 10 && walletBalance != null && (
                <div className="mb-3 p-3" style={{ background: "#f4f7fb", borderRadius: 12 }}>
                  <div className="d-flex justify-content-between">
                    <span>Wallet</span>
                    <strong>{rupee(walletUse)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Online payment</span>
                    <strong>{rupee(onlineUse)}</strong>
                  </div>
                </div>
              )}
{instantBonus > 0 && (
                <p className="small mb-3" style={{ color: "#1f7a4d" }}>
                  *Instant bonus {rupee(instantBonus)} wil be credited to your wallet"
                </p>
              )}
              {selected && (
                <p className="text-muted small">
                  {selected.name} · {selected.service} · skey {selected.skey}
                </p>
              )}
  {maxRechargeReached && (
                <div className="alert alert-info py-2" role="status">
                  Your max recharge amount is Rs.{maxRechargeLabel}.
                </div>
              )}
              <button
                type="submit"
                className="btn w-100 text-white"
                style={{ background: "#5ca4d6", borderRadius: 12, fontWeight: 700 }}
                disabled={submitting || rechargeAmount < 10 || maxRechargeReached}
              >
                {submitting
                  ? "Please wait..."
                  : autoMode === "partial"
                    ? `Recharge · Wallet ${rupee(walletUse)} + Online ${rupee(onlineUse)}`
                    : autoMode === "wallet"
                      ? `Recharge · Wallet ${rupee(walletUse)}`
                      : `Pay full ${rupee(rechargeAmount || 0)} online`}
              </button>
              {autoMode !== "razorpay" && rechargeAmount >= 10 && (
                <button
                  type="button"
                  className="btn w-100 mt-2"
                  style={{ borderRadius: 12, fontWeight: 700, border: "1px solid #5ca4d6", color: "#5ca4d6" }}
                  disabled={submitting}
                  onClick={() => submitRecharge("razorpay")}
                >
                  Pay full {rupee(rechargeAmount)} online
                </button>
              )}



              <p className="text-muted small mt-2 mb-0">
              *Note: Direct online Recharges without applying minimum required reward points from wallet is NOT eligible for any reward points scheme(Such as NO instant reward points, NO APM MONTHLY BONUS, NO milestone achievers GIFT points)
              <br></br>
              *Any plans can be recharged w/o any restrictions similar to other platforms.
              </p>
            </form>

            </>
            )}

            {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
            {result && (
              <div className={`alert mt-3 mb-0 ${result.status === "failed" ? "alert-danger" : "alert-success"}`}>
                <div className="fw-semibold text-capitalize">{result.status}</div>
                <div>{result.message}</div>
                {result.status !== "failed" && result.pay_mode !== "razorpay" && (
                  <div className="mt-2">
                    <div className="fw-semibold">Purchase bill</div>
                    <div>Bill : {result.orderid} · {result.operator}</div>
                    <div>Bill amount: ₹{Number(result.amount).toLocaleString("en-IN")}</div>
                    {result.account_no && <div>Number: {result.account_no}</div>}
                  </div>
                )}
                {result.status !== "failed" && result.pay_mode === "razorpay" && (
                  <div className="mt-2">Full online payment. Saved in recharge history only.</div>
                )}
                {result.orderid && result.status === "failed" && <div>Order ID: {result.orderid}</div>}
                {result.opid && <div>Operator ID: {result.opid}</div>}
                {result.points_deducted && Number(result.points_deducted) > 0 && (
                  <div>Wallet used: ₹{result.points_deducted}</div>
                )}
                {result.online_amount > 0 && <div>Razorpay paid: ₹{result.online_amount}</div>}
                {result.redemption_balance != null && (
                  <div>Points available for redemption: ₹{result.redemption_balance}</div>
                )}
                {Number(result.instant_bonus) > 0 && (
                  <div>
                    Instant bonus credited: ₹{Number(result.instant_bonus).toLocaleString("en-IN")}
                    {result.bonus_expires_at
                      ? ` · expires ${new Date(result.bonus_expires_at).toLocaleDateString("en-IN")}`
                      : ""}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {history.length > 0 && (
          <div className="card shadow-sm mt-3" style={{ borderRadius: 16, border: "none" }}>
            <div className="card-body">
              <h6 className="fw-bold">Recent recharges</h6>
              {visibleHistory.map((row) => (
                <div key={row.orderid} className="border-bottom py-2">
                  <div className="d-flex justify-content-between">
                    <strong>{row.operator_name}</strong>
                    <span>₹{row.amount}</span>
                  </div>
                  <div className="small text-muted">
                    {formatRechargeWhen(row.created_at)}
                  </div>
                  <div className="small text-muted">
                    {row.account_no} · {row.skey} · {row.status}
                    {row.pay_mode === "razorpay" ? " · Full online" : ""}
                  </div>
                  <div className="small text-muted">{row.orderid}</div>
                </div>
              ))}
              {canLoadMore && (
                <button
                  type="button"
                  className="btn w-100 mt-3"
                  style={{ borderRadius: 12, fontWeight: 700, border: "1px solid #5ca4d6", color: "#5ca4d6" }}
                  disabled={historyLoading}
                  onClick={() => {
                    if (serverPaging) loadHistory(historyPage + 1);
                    else setHistoryShown((count) => count + 10);
                  }}
                >
                  {historyLoading ? "Loading..." : "Load more"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <Modal show={showWalletInfo} onHide={() => setShowWalletInfo(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #e6eef5" }}>
          <Modal.Title className="fs-5">Recharge T&C</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">*welcome bonus can't be used for Recharges and CC Bill payments.
          </p>
          <p className="mb-0">*Referral bonus can be used for Recharges and Bill payments.</p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #e6eef5" }}>
          <button
            type="button"
            className="btn text-white"
            style={{ background: "#5ca4d6", borderRadius: 12, fontWeight: 700 }}
            onClick={() => setShowWalletInfo(false)}
          >
            OK
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPlans} onHide={() => setShowPlans(false)} centered scrollable>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #e6eef5" }}>
          <Modal.Title className="fs-5">
            {selected ? selected.name : "Plans"}
            {circle?.name ? ` · ${circle.name}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f4f7fb", maxHeight: "70vh" }}>
          {planTypes.length > 0 && (
            <div
              className="mb-3"
              style={{ display: "flex", gap: 8, overflowX: "auto", whiteSpace: "nowrap", paddingBottom: 4 }}
            >
              {[["All", plans.length], ...planTypes].map(([type, count]) => {
                const active = planType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setPlanType(type)}
                    style={{
                      flex: "0 0 auto",
                      borderRadius: 999,
                      border: active ? "1px solid #5ca4d6" : "1px solid #d5e3ef",
                      background: active ? "#5ca4d6" : "#fff",
                      color: active ? "#fff" : "#335",
                      fontWeight: 700,
                      fontSize: 13,
                      padding: "8px 14px",
                    }}
                  >
                    {type} · {count}
                  </button>
                );
              })}
            </div>
          )}
          <input
            className="form-control mb-3"
            value={planQuery}
            onChange={(e) => setPlanQuery(e.target.value)}
            placeholder="Search amount or details"
          />
          {visiblePlans.length === 0 && (
            <p className="text-muted small mb-0">No plans match this search.</p>
          )}
          {visiblePlans.map((plan) => (
            <button
              key={`${plan.amount}-${plan.validity}-${plan.index}`}
              type="button"
              className="btn w-100 text-start mb-2"
              style={{
                borderRadius: 12,
                border: "1px solid #d5e3ef",
                background: "#fff",
                whiteSpace: "normal",
                height: "auto",
                padding: "12px 14px",
              }}
              onClick={() => {
                setAmount(String(plan.amount));
                setSelectedPlan(plan);
                setShowPlans(false);
              }}
            >
              <div className="d-flex justify-content-between align-items-start">
                <strong>₹{Number(plan.amount).toLocaleString("en-IN")}</strong>
                <span className="small text-muted text-end">
                  {plan.validity ? `validity: ${plan.validity}` : ""}
                  {service === "Prepaid" ? `${plan.validity ? " · " : ""}Talktime : ${formatTalktime(plan.talktime)}` : ""}
                </span>
              </div>
              {plan.typeLabel && <div className="small" style={{ color: "#5ca4d6" }}>{plan.typeLabel}</div>}
              {plan.description && <div className="small text-muted mt-1" style={{ whiteSpace: "pre-line" }}>{plan.description}</div>}
            </button>
          ))}
        </Modal.Body>
      </Modal>
    </div>
  );
}

const navStyle = {
  background: "#5ca4d6",
  borderBottomLeftRadius: "16px",
  borderBottomRightRadius: "16px",
  padding: "18px 12px",
};
